/**
 * Combo, payout, rank. Pure functions over the tuning block — no state, no THREE, no bus.
 *
 * The economy is the game. Crazy Taxi lives or dies on the moment the fare pays out and the
 * clock jumps; here the repair pays TIME first and cash second, and the combo multiplier is
 * what turns a competent run into a long one.
 */

/**
 * Combo multiplier. 1 + linear·c + quad·c².
 * Generous early (your second repair already pays ~2×, so the chain hooks you immediately)
 * and steep late (c=12 pays ~6×, c=20 hits the cap), which is what makes a long chain worth
 * protecting and a break worth swearing about.
 */
export function comboMultiplier(combo, t) {
  if (combo <= 0) return 1;
  const m = 1 + t.linear * combo + t.quad * combo * combo;
  return m > t.maxMultiplier ? t.maxMultiplier : m;
}

/** Highest tier crossed at this combo, or 0. Drives the announcer barks. */
export function comboTier(combo, t) {
  let tier = 0;
  for (let i = 0; i < t.tiers.length; i++) if (combo >= t.tiers[i]) tier = t.tiers[i];
  return tier;
}

/** Slalom chain feeds the NEXT repair — this is what ties the driving to the repairing. */
export function slalomMultiplier(chain, t) {
  const c = chain > t.chainCap ? t.chainCap : chain;
  return 1 + c * t.perLink;
}

/**
 * What one completed repair is worth.
 *
 * TIME is the reward that matters: base + perfect bonus, scaled by a SOFTENED multiplier.
 * Cash and score take the full multiplier. If time took the full multiplier too, a chain of
 * eight would hand you 45 seconds a hole and the countdown would stop being frightening —
 * which would break the only pressure the game has. `timeMultiplierWeight` is the knob.
 */
export function repairPayout(quality01, comboMul, slalomMul, perfect, t) {
  const effMul = comboMul * slalomMul;

  const timeMulRaw = 1 + (effMul - 1) * t.timeMultiplierWeight;
  const timeMul = timeMulRaw > t.timeMultiplierCap ? t.timeMultiplierCap : timeMulRaw;
  // Time now pays for QUALITY as well as for the chain — see TUNING.payout.timeQualityFloor.
  const qMul = t.timeQualityFloor + t.timeQualitySpan * (quality01 || 0);
  const seconds = (t.baseSeconds * qMul + (perfect ? t.perfectSeconds : 0)) * timeMul;

  const cash = t.baseCash * effMul * (t.cashQualityFloor + t.cashQualitySpan * quality01);
  const score = Math.round(cash * t.scorePerDollar) + (perfect ? t.perfectScoreBonus : 0);

  return {
    seconds: Math.round(seconds * 10) / 10,
    cash: Math.round(cash * 100) / 100,
    score,
    effMul,
    timeMul,
  };
}

/** Rank ladder, worst first. Index into this is the single number the RESULT screen uses. */
export const RANKS = Object.freeze(['C', 'B', 'A', 'S']);

/**
 * ONE NUMBER DRIVES THE BADGE AND THE STARS.
 *
 * Round 6 defect: "CLASSE shows an S badge alongside 4 of 5 filled stars. S is conventionally
 * above A and should imply a full row." The two were computed from different quantities —
 * the letter from `score`, the star row from `repaired / total` — so at 68 420 points and
 * 21 of 24 holes they disagreed, and a disagreement in a results screen reads as a bug.
 *
 * The rank is now gated by completion as well as score, so the ladder and the star row are
 * monotone in the same quantity and cannot contradict each other:
 *   C ≥ 0 %,  B ≥ 40 %,  A ≥ 60 %,  S = the whole board.
 * 21/24 = 87.5 % therefore reads A + 4 stars, which is exactly what the screen shows.
 */
export function capRankByCompletion(rank, completion01, t) {
  const floors = t.rankCompletion;
  if (!floors) return rank;
  let i = RANKS.indexOf(rank);
  if (i < 0) i = 0;
  const c = completion01 != null ? completion01 : 1;
  while (i > 0 && c + 1e-6 < floors[i]) i--;
  return RANKS[i];
}

/** S must feel earned — a full clean board with a chain kept alive. */
export function rankFor(score, t, completion01) {
  const base = score >= t.rankS ? 'S' : score >= t.rankA ? 'A' : score >= t.rankB ? 'B' : 'C';
  return capRankByCompletion(base, completion01, t);
}

/**
 * The star row expressed from the SAME number as the letter, so a consumer that wants one
 * source of truth has one. C→2, B→3, A→4, S→5 of 5.
 */
export function starsForRank(rank, count) {
  let i = RANKS.indexOf(rank);
  if (i < 0) i = 0;
  const n = count - (RANKS.length - 1) + i;
  return n < 0 ? 0 : (n > count ? count : n);
}

/**
 * The running tally. Plain object so the harness can force any field and the UI can read it
 * without a copy.
 */
export function makeScoreState() {
  return {
    score: 0,
    cash: 0,
    combo: 0,
    multiplier: 1,
    bestCombo: 0,
    perfects: 0,
    repaired: 0,
    thumps: 0,
    conesHit: 0,
    threads: 0,
    slalomChain: 0,
    bestSlalom: 0,
    secondsEarned: 0,
    ruined: 0,
  };
}

export function resetScoreState(s) {
  s.score = 0; s.cash = 0; s.combo = 0; s.multiplier = 1;
  s.bestCombo = 0; s.perfects = 0; s.repaired = 0; s.thumps = 0;
  s.conesHit = 0; s.threads = 0; s.slalomChain = 0; s.bestSlalom = 0;
  s.secondsEarned = 0; s.ruined = 0;
}
